import { ResponsiveSwarmPlot } from '@nivo/swarmplot'

import { useQuery } from '@redwoodjs/web'

import { useCallDrawerContext } from 'src/context/CallDrawerContext'
import { VoiceAgentAnalysisResultName } from 'src/graphql/types/voiceAgentResult'
import { GET_VOICE_AGENT_ANALYSIS_DISTRIBUTION } from 'src/graphql/voiceAgentAnalysisResult.graphql'
import { formatSnakeValue } from 'src/lib/formatters'
import { voiceAgentAnalysisColors } from 'src/pages/CallAnalyticsPage/utils'

type SwarmPlotData = {
  group: VoiceAgentAnalysisResultName
  duration: number
  activityLogId: string
}

const VoiceAgentSwarmPlot = ({ filters }) => {
  const { handleOpen } = useCallDrawerContext()

  const { data: distributionData, loading: distributionLoading } = useQuery(
    GET_VOICE_AGENT_ANALYSIS_DISTRIBUTION,
    {
      variables: {
        filters,
        limit: 20,
      },
    }
  )

  const distributionResults =
    distributionData?.voiceAgentAnalysisDistribution || []

  const swarmPlotData: SwarmPlotData[] = distributionResults
    .filter((result) => result.activityLog?.callDuration)
    .map((result) => ({
      group: result.name,
      duration: result.activityLog.callDuration,
      activityLogId: result.activityLog.activityLogId,
    }))

  return (
    <div className="flex h-[250px] flex-row items-center justify-between">
      {swarmPlotData.length === 0 ? (
        <div className="flex w-full flex-col items-center justify-center text-sm font-medium text-doubleNickel-gray-500">
          No data available
        </div>
      ) : (
        <ResponsiveSwarmPlot<SwarmPlotData>
          data={swarmPlotData}
          groups={Object.values(VoiceAgentAnalysisResultName)}
          value="duration"
          valueFormat={(value: number) => `${(value / 60).toFixed(1)}m`}
          valueScale={{ type: 'linear', min: 0, max: 'auto' }}
          size={12}
          layout="horizontal"
          forceStrength={4}
          simulationIterations={100}
          colors={({ group }) => voiceAgentAnalysisColors[group]}
          borderColor={{
            from: 'color',
            modifiers: [
              ['darker', 0.6],
              ['opacity', 0.5],
            ],
          }}
          margin={{ bottom: 60, left: 120, right: 30 }}
          axisTop={null}
          enableGridX={false}
          axisRight={null}
          axisBottom={{
            tickSize: 10,
            tickPadding: 5,
            tickRotation: 0,
            legend: 'Call Length (Seconds)',
            legendPosition: 'middle',
            legendOffset: 46,
            truncateTickAt: 0,
          }}
          axisLeft={{
            tickSize: 10,
            tickPadding: 5,
            tickRotation: 0,
            legendPosition: 'middle',
            legendOffset: -76,
            truncateTickAt: 0,
            format: (value) => formatSnakeValue(value),
          }}
          tooltip={({ data }) => (
            <div className="rounded bg-white p-2 shadow">
              <strong>{formatSnakeValue(data.group)}</strong>:{' '}
              {(data.duration / 60).toFixed(1)}m
            </div>
          )}
          onClick={({ data }) => handleOpen(data.activityLogId)}
        />
      )}
    </div>
  )
}

export default VoiceAgentSwarmPlot
